import { getKeySetSize, KeySet } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogProps, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../../gen-src/Localize';
import { ApiRequestsTypes } from '../../apiRequests/ApiRequests';
import { useText } from '../../hooks/useText';
import { useToggle } from '../../hooks/useToggle';
import { ValidTelemetryEvent } from '../../telemetry/TelemetrySender';
import { ComposeEmailDialogContent, ComposeEmailDialogFooter } from './ComposeEmailDialogContent';
import { ComposeEmailDialogStyles } from './ComposeEmailDialogStyles';
import { ImpactAnalysisEmailTelemetryEvent } from './ImpactAnalysisEmailTelemetryEvent';

export interface DialogResultParams {
  subjectText: string;
  bodyText: string;
}

export interface ComposeEmailDialogContainerProps {
  readonly selectedUserIds: KeySet;
  readonly dismissDialog: () => void;
  readonly submitResult: (args: DialogResultParams) => void;
  readonly contentType: ApiRequestsTypes.SendImpactAnalysisEmailContentType;
  readonly contentLuid: string;
  readonly dialogSessionId: string;
  readonly sendDataInImpactAnalysisEmail: (event: Omit<ValidTelemetryEvent<ImpactAnalysisEmailTelemetryEvent>, 'type'>) => void;
}

export function ComposeEmailDialogContainer(props: ComposeEmailDialogContainerProps): JSX.Element {
  const [isSubmitting, toggleIsSubmitting] = useToggle();
  const subjectText = useText('', ComposeEmailDialogStyles.MAXIMUM_SUBJECT_LENGTH);
  const bodyText = useText('', ComposeEmailDialogStyles.MAXIMUM_BODY_LENGTH);

  const handleBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    bodyText.handleTextChange(event)
  }

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    subjectText.handleTextChange(event);
  }

  const dialogProps = (): DialogProps => {
    return {
      title: Messages.EmailDialogTitle(),
      dialogWidth: ComposeEmailDialogStyles.DIALOG_WIDTH,
      testId: ComposeEmailDialogStyles.TEST_ID('dialog'),
      isModal: true,
      handleGlassClick: props.dismissDialog,
      type: DialogType.NormalWithTitleBar,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldAdjustPositionOnWindowResize: true,
      onCloseRequest: props.dismissDialog,
      showCloseButton: false,
      showFooter: true,
      footerContent: (
        <ComposeEmailDialogFooter
          dismissDialog={props.dismissDialog}
          invokeSubmit={invokeSubmit}
          isSubmitting={isSubmitting}
          isValid={bodyText.text.textValidity === 'valid' && subjectText.text.textValidity === 'valid'}
        />
      ),
    };
  }

  const invokeSubmit = () => {
    toggleIsSubmitting();
    props.submitResult({
      bodyText: bodyText.text.text,
      subjectText: subjectText.text.text
    })

    props.sendDataInImpactAnalysisEmail({
      content_luid: props.contentLuid,
      content_type: props.contentType,
      dialog_session_id: props.dialogSessionId,
      user_count: getKeySetSize(props.selectedUserIds),
    });
  }

  return (
    <DialogWidget {...dialogProps()}>
      <ComposeEmailDialogContent
        subjectValidity={subjectText.text.textValidity}
        bodyValidity={bodyText.text.textValidity}
        selectedUserIds={props.selectedUserIds}
        bodyText={bodyText.text.text}
        subjectText={subjectText.text.text}
        handleBodyChange={handleBodyChange}
        handleSubjectChange={handleSubjectChange}
      />
    </DialogWidget>
  );
}
